import * as React from 'react'
import moment from 'moment'
import * as Tooltip from 'react-tooltip'
import { message } from 'antd'
import { inject, observer } from 'mobx-react'
import { Alert } from 'reactstrap'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { CreateConversionModal } from './CreateConversionModal'
import {
  IListConversion,
  CreateConversion,
  ConversionStatus,
  API,
  productName,
} from '@getgreenline/homi-shared'
import { StatusBadge } from '../StatusBadge'
import { AppView, AppMainView } from '../../../../components/AppView'
import { Sidebar, SidebarHeader, SidebarContent } from '../../../../components/AppView/Sidebar'
import { LoadingAnimation } from '../../../../components/LoadingAnimation'
import { Button } from '../../../../components/Button'
import AppHeader from '../../../../components/AppView/Header'
import { DebounceInput } from '../../../../components/Input'
import { SellableProductAsyncInput } from '../../../../components/ProductAsyncInput/SellableProductAsyncInput'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'
import Helmet from 'react-helmet'
import { BLAZE_COMPANY } from '../../../../constants/general'

interface Props {
  currentCompanyStore: CurrentCompanyStore
}

interface State {
  conversions?: IListConversion[]
  statusFilter?: ConversionStatus
  isSaving: boolean
  showCreateModal: boolean
  showCount: number
  conversionIdSearch: string
}

@inject('currentCompanyStore')
@observer
export class Conversion extends React.Component<Props, State> {
  conversion = new CreateConversion()

  constructor(props: Props) {
    super(props)
    this.state = {
      conversions: undefined,
      isSaving: false,
      showCreateModal: false,
      showCount: 20,
      conversionIdSearch: '',
    }
  }

  componentDidMount() {
    this.loadConversions()
  }

  loadConversions() {
    const company = this.props.currentCompanyStore!.company!
    API.getConversions(company.id).then((conversions) => {
      this.setState({ conversions: conversions })
    })
  }

  loadConversionById(conversionId: string) {
    const company = this.props.currentCompanyStore!.company!
    this.conversion.id = null
    API.getConversionById(company.id, conversionId).then((conversion) => {
      this.conversion.fullPopulate(conversion)
    })
  }

  save() {
    if (this.conversion.id) {
      const company = this.props.currentCompanyStore!.company!
      API.updateConversion(company.id, this.conversion.id, this.conversion).then((conversion) => {
        this.conversion.fullPopulate(conversion)
        this.loadConversions()
        message.success(`Saved`)
      })
    }
  }

  async complete() {
    if (this.conversion.id) {
      if (window.confirm('Inventory will be checked in. Are you sure?')) {
        const company = this.props.currentCompanyStore!.company!
        await API.updateConversion(company.id, this.conversion.id, this.conversion)
        const completedConversion = await API.completeConversion(company.id, this.conversion.id)
        this.conversion.fullPopulate(completedConversion)
        this.loadConversions()
      }
    }
  }

  cancel() {
    if (this.conversion.id) {
      if (window.confirm('Inventory changes will be reversed. Are you sure?')) {
        const company = this.props.currentCompanyStore!.company!
        API.cancelConversion(company.id, this.conversion.id).then((conversion) => {
          this.conversion.fullPopulate(conversion)
          this.loadConversions()
        })
      }
    }
  }

  statusFilter(filter: ConversionStatus | undefined) {
    if (this.state.statusFilter === filter) {
      this.setState({
        statusFilter: undefined,
      })
    } else {
      this.setState({
        statusFilter: filter,
      })
    }
  }

  render() {
    const { conversionIdSearch } = this.state

    const company = this.props.currentCompanyStore!.company!
    const conversions = this.state.conversions
    let filteredConversions = conversions
    if (filteredConversions && this.state.statusFilter) {
      filteredConversions = filteredConversions.filter((c) => c.status === this.state.statusFilter)
    }
    if (filteredConversions && this.state.conversionIdSearch) {
      const lowerCaseConversionSearch = conversionIdSearch.toLowerCase()
      filteredConversions = filteredConversions.filter(
        (conversion) =>
          conversion.id.toLowerCase().includes(lowerCaseConversionSearch) ||
          conversion.title.toLowerCase().includes(lowerCaseConversionSearch),
      )
    }

    if (!company.permissions.canAccessInventoryConversions) {
      return (
        <div className='d-flex p-4' style={{ alignItems: 'center', justifyContent: 'center' }}>
          <div>
            <div className='badge badge-warning mb-2'>BETA</div>
            <h3>This feature is currently in beta</h3>
            <p className='text-muted'>Please contact {BLAZE_COMPANY} support to request access</p>
          </div>
        </div>
      )
    }

    const filterStatusOptions: ConversionStatus[] = [
      'pending',
      'inProgress',
      'completed',
      'cancelled',
    ]

    const filters = (
      <div className='status-filters'>
        {filterStatusOptions.map((statusFilter, i) => (
          <Button
            key={i}
            classes={['status-filters__btn']}
            active={statusFilter === this.state.statusFilter}
            onClick={() => this.statusFilter(statusFilter)}
          >
            {statusFilter}
          </Button>
        ))}
      </div>
    )

    const conversionsToDisplay = (filteredConversions || []).slice(0, this.state.showCount)

    const leftSidebarContents: JSX.Element = (
      <Sidebar>
        <SidebarHeader>
          <div className='ml-2 mb-2 mt-2'>
            <DebounceInput
              label='Filter by ID or Title'
              labelStyle={{ fontSize: 14 }}
              value={this.state.conversionIdSearch}
              onChange={(e) => {
                this.setState({ conversionIdSearch: e.target.value })
              }}
            />
          </div>
          {filters}
        </SidebarHeader>
        <SidebarContent>
          {!conversions ? (
            <LoadingAnimation />
          ) : conversionsToDisplay.length > 0 ? (
            conversionsToDisplay.map((conversion) => {
              const isSelected = this.conversion.id ? this.conversion.id === conversion.id : false
              return (
                <div
                  key={conversion.id}
                  className={`view-control ` + (isSelected ? 'active' : '')}
                  style={{ position: 'relative' }}
                  onClick={() => this.loadConversionById(conversion.id)}
                >
                  <div style={{ position: 'absolute', top: 5, right: 5 }}>
                    <span className='text-muted'>ID: {conversion.id}</span>
                  </div>
                  <div>
                    <small className='text-muted'>
                      {moment(conversion.createDate).format('MMM Do YYYY h:mm a')}
                    </small>
                  </div>
                  <div className='view-control__title'>{conversion.title}</div>
                  <StatusBadge status={conversion.status} />
                </div>
              )
            })
          ) : (
            <div style={{ padding: '1rem' }}>No conversions available</div>
          )}
          <br />
          {(conversions || []).length > this.state.showCount && (
            <button
              className='btn btn-light w-100'
              onClick={() => this.setState({ showCount: this.state.showCount + 20 })}
            >
              Show more
            </button>
          )}
        </SidebarContent>
      </Sidebar>
    )

    let mainBodyContents: JSX.Element
    if (!this.conversion.id) {
      mainBodyContents = (
        <div className='container p-4'>
          <br />
          <div>
            <span className='fa fa-filter fa-4x' />
          </div>
          <br />
          <h3>Conversions</h3>
          <br />
          <div>Track your inventory as they become new products.</div>
          <div>For instance, turn bulk kush products into pre-rolls, and bottoms/waste</div>
          <div>or turn bulk kush into hash/shatter.</div>
          <br />
          <div>Select or add a conversion to start</div>
        </div>
      )
    } else {
      const selectedConversion = this.conversion

      mainBodyContents = (
        <div className='pb-2'>
          {this.conversion.status === 'pending' && (
            <>
              <Alert color='warning'>This conversion has not been completed yet</Alert>
              <Alert color='warning'>
                <span className='fa fa-exclamation-triangle mr-2' />
                Conversions do not save automatically anymore. Please press the 'Save' button when
                updating numbers.
              </Alert>
            </>
          )}
          {this.conversion.status === 'completed' && (
            <Alert color='success'>This conversion has been completed</Alert>
          )}
          {this.conversion.status === 'cancelled' && (
            <Alert color='danger'>This conversion has been cancelled</Alert>
          )}

          <table className='table'>
            <tbody>
              <tr>
                <td>ID</td>
                <td className='text-muted'>{selectedConversion.id}</td>
              </tr>
              <tr>
                <td>Title</td>
                <td>
                  <input
                    className='form-control'
                    value={this.conversion.title || ''}
                    onChange={(e) => (this.conversion.title = e.target.value || '')}
                  />
                </td>
              </tr>
              <tr>
                <td>Location</td>
                <td>{selectedConversion.locationName}</td>
              </tr>
              <tr>
                <td>Created by</td>
                <td>{selectedConversion.startingUserName}</td>
              </tr>
              <tr>
                <td>
                  <span className='mr-2'>Variance</span>
                  <span
                    className='fa fa-exclamation-circle text-muted'
                    data-tip
                    data-for='variance'
                  />
                  <Tooltip id='variance' place='right' effect='solid'>
                    <div className='font-weight-normal'>
                      <div>
                        Represents the weight difference between your source and final products.
                      </div>
                      <div>Useful for pre-roll conversions.</div>
                    </div>
                  </Tooltip>
                </td>
                <td>{selectedConversion.variance} grams</td>
              </tr>
              <tr>
                <td>Notes</td>
                <td>
                  <textarea
                    rows={2}
                    className='form-control'
                    value={this.conversion.notes || ''}
                    onChange={(e) => (this.conversion.notes = e.target.value || '')}
                  />
                </td>
              </tr>
            </tbody>
          </table>

          <div className='row'>
            <div className='col-md-3'>
              <div>Source products</div>
              <div className='text-muted'>Inventory will be removed</div>
            </div>

            <div className='col-md-9'>
              <div>
                {selectedConversion.status === 'pending' ? (
                  <div className='mb-2' style={{ width: 300 }}>
                    <SellableProductAsyncInput
                      doesNotAllowBatchTrackedProducts
                      isInventoryProducts
                      clearable={false}
                      placeholder='Add product'
                      onSelect={(product) => {
                        const productWeight =
                          product.unit === 'gram'
                            ? product.inventorySubtractAmount
                            : product.weight || 0
                        this.conversion.inProducts.push({
                          productId: product.id,
                          productName: productName(product),
                          amount: 0,
                          productUnit: product.unit,
                          productWeight: productWeight,
                          weight: 0,
                        })
                      }}
                    />
                  </div>
                ) : undefined}
                <table className='table table-sm'>
                  <thead className='thead-light'>
                    <tr>
                      <th>Product</th>
                      <th>Quantity</th>
                      <th>Weight</th>
                      <th>Options</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedConversion.inProducts.map((inProduct, index) => {
                      return (
                        <tr key={inProduct.productId}>
                          <td>{inProduct.productName}</td>
                          <td>
                            {selectedConversion.status === 'pending' ? (
                              <input
                                className='form-control'
                                style={{ width: '8em' }}
                                type='number'
                                value={inProduct.amount}
                                onChange={(e: any) => {
                                  const newAmount = parseFloat(e.target.value) || 0
                                  inProduct.amount = newAmount
                                  inProduct.weight = newAmount * inProduct.productWeight
                                }}
                              />
                            ) : (
                              <span>
                                {inProduct.amount} {inProduct.productUnit}s
                              </span>
                            )}
                          </td>
                          <td>
                            <span className='mr-2'>{inProduct.weight} grams</span>
                            {inProduct.productWeight === 0 && (
                              <span>
                                <span
                                  className='fa fa-exclamation-circle text-warning'
                                  data-tip
                                  data-for='no-weight'
                                />
                                <Tooltip id='no-weight' place='right' effect='solid'>
                                  <div>This product does not have any weights attached.</div>
                                  <div>
                                    To estimate variances, please edit this product to include a
                                    weight.
                                  </div>
                                </Tooltip>
                              </span>
                            )}
                          </td>
                          <td style={{ maxWidth: '5em' }}>
                            {selectedConversion.status === 'pending' && (
                              <a
                                onClick={() => {
                                  selectedConversion.inProducts.splice(index, 1)
                                }}
                              >
                                Remove
                              </a>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className='col-md-3'>
              <div>Final products</div>
              <div className='text-muted'>Inventory will be added</div>
            </div>

            <div className='col-md-9'>
              <div>
                {selectedConversion.status !== 'completed' ? (
                  <div className='mb-2' style={{ width: 300 }}>
                    <SellableProductAsyncInput
                      doesNotAllowBatchTrackedProducts
                      isInventoryProducts
                      clearable={false}
                      placeholder='Add product'
                      onSelect={(product) => {
                        const productWeight =
                          product.unit === 'gram'
                            ? product.inventorySubtractAmount
                            : product.weight || 0
                        this.conversion.outProducts.push({
                          productId: product.id,
                          productName: productName(product),
                          amount: 0,
                          productUnit: product.unit,
                          productWeight: productWeight,
                          weight: 0,
                        })
                      }}
                    />
                  </div>
                ) : undefined}
                <table className='table table-sm'>
                  <thead className='thead-light'>
                    <tr>
                      <th>Product</th>
                      <th>Quantity</th>
                      <th>Weight</th>
                      <th>Options</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedConversion.outProducts.map((outProduct, index) => {
                      return (
                        <tr key={outProduct.productId}>
                          <td>{outProduct.productName}</td>
                          <td>
                            {selectedConversion.status === 'completed' ? (
                              <span>
                                {outProduct.amount} {outProduct.productUnit}s
                              </span>
                            ) : (
                              <input
                                className='form-control'
                                style={{ width: '5em' }}
                                type='number'
                                value={outProduct.amount}
                                onChange={(e: any) => {
                                  const newAmount = parseFloat(e.target.value) || 0
                                  outProduct.amount = newAmount
                                  outProduct.weight = newAmount * outProduct.productWeight
                                }}
                              />
                            )}
                          </td>
                          <td>
                            <span className='mr-2'>{outProduct.weight} grams</span>
                            {outProduct.productWeight === 0 && (
                              <span>
                                <span
                                  className='fa fa-exclamation-circle text-warning'
                                  data-tip
                                  data-for='no-weight'
                                />
                                <Tooltip id='no-weight' place='right' effect='solid'>
                                  <div>This product does not have any weights attached.</div>
                                  <div>
                                    To estimate variances, please edit this product to include a
                                    weight.
                                  </div>
                                </Tooltip>
                              </span>
                            )}
                          </td>
                          <td style={{ maxWidth: '5em' }}>
                            {selectedConversion.status === 'pending' ||
                            selectedConversion.status === 'inProgress' ? (
                              <a
                                onClick={() => {
                                  selectedConversion.outProducts.splice(index, 1)
                                }}
                              >
                                Remove
                              </a>
                            ) : undefined}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <br />

          <div className='text-right'>
            <div className='mb-2'>
              <button
                className='btn btn-lg btn-success'
                style={{ width: 270 }}
                onClick={() => this.save()}
              >
                Save
              </button>
            </div>
            <div>
              <button className='btn btn-lg btn-light mr-2' onClick={() => this.cancel()}>
                Cancel
                <span
                  className='ml-2 fa fa-question-circle text-muted'
                  data-tip
                  data-for='cancel'
                />
                <Tooltip id='cancel' place='right' effect='solid'>
                  <div className='font-weight-normal'>
                    <div>Reverse all inventory changes and void this conversion.</div>
                    <div>Cancelled conversions cannot be edited.</div>
                  </div>
                </Tooltip>
              </button>
              {this.conversion.status === 'pending' && (
                <button className='btn btn-lg btn-light' onClick={() => this.complete()}>
                  Complete
                  <span
                    className='ml-2 fa fa-question-circle text-muted'
                    data-tip
                    data-for='complete'
                  />
                  <Tooltip id='complete' place='right' effect='solid'>
                    <div className='font-weight-normal'>
                      <div>Source products will be removed, and final products will be added.</div>
                      <div>Completed products cannot be edited.</div>
                    </div>
                  </Tooltip>
                </button>
              )}
            </div>
          </div>

          <div style={{ height: 300 }} />
        </div>
      )
    }

    return (
      <AppView column>
        <Helmet>
          <title>Inventory | Conversions</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <AppHeader title='Conversions'>
          <BlazeButton
            buttonProps={{
              type: 'primary',
              icon: 'plus',
              onClick: () => this.setState({ showCreateModal: true }),
            }}
            titleProps={{
              title: 'Add conversion',
            }}
          />
        </AppHeader>
        <AppView>
          {leftSidebarContents}
          <AppMainView>
            <div className='container'>{mainBodyContents}</div>
          </AppMainView>
        </AppView>
        {this.state.showCreateModal && (
          <CreateConversionModal
            onSubmit={(conversion) => {
              this.loadConversions()
              this.loadConversionById(conversion.id)
              this.setState({ showCreateModal: false })
            }}
            onClose={() => this.setState({ showCreateModal: false })}
          />
        )}
      </AppView>
    )
  }
}
